import { RESPONSIVE_IMAGES } from "@/lib/images.generated";

export interface ResponsiveImageProps {
  /** Original CDN master URL. Falls back to a plain <img> when no variants exist. */
  src: string;
  alt: string;
  className?: string;
  /** The `sizes` attribute — describe the rendered width, not the file width. */
  sizes?: string;
  /** LCP candidates must pass priority so the browser fetches them eagerly. */
  priority?: boolean;
}

function srcSet(variants: { width: number; url: string }[]) {
  return variants.map((v) => `${v.url} ${v.width}w`).join(", ");
}

/**
 * Renders a CDN image as AVIF → WebP → original PNG/JPEG.
 *
 * The Lovable asset CDN serves immutable objects and has no on-the-fly
 * transform (`?width=` / `?format=` are ignored), so variants are pre-built
 * and uploaded by `scripts/perf/generate-image-manifest.mjs`. Masters are
 * preserved untouched and remain the final `<img src>` fallback, so nothing
 * regresses for a browser without AVIF/WebP support.
 */
export function ResponsiveImage({
  src,
  alt,
  className = "",
  sizes = "(max-width: 640px) 92vw, (max-width: 1024px) 45vw, 400px",
  priority = false,
}: ResponsiveImageProps) {
  const entry = RESPONSIVE_IMAGES[src];

  const img = (
    <img
      src={src}
      alt={alt}
      className={className}
      width={entry?.intrinsicWidth}
      height={entry?.intrinsicHeight}
      sizes={entry ? sizes : undefined}
      srcSet={entry ? srcSet(entry.webp) : undefined}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
      decoding={priority ? "sync" : "async"}
    />
  );

  if (!entry) return img;

  return (
    <picture>
      <source type="image/avif" srcSet={srcSet(entry.avif)} sizes={sizes} />
      <source type="image/webp" srcSet={srcSet(entry.webp)} sizes={sizes} />
      {img}
    </picture>
  );
}
